import { fmtNumber } from "../lib/api.ts";

export interface Turn {
  index: number;
  schema: { tokens: number; bytes: number; source: "api" | "estimate" };
  messages: { tokens: number; bytes: number; source: "api" | "estimate" };
  total: number;
  toolsListed: number;
  action: string;
}

/**
 * Hand-rolled SVG. Same discipline as the deck: no chart library for a stacked
 * bar chart that is forty lines of arithmetic.
 */
export function TokenMeter({
  turns,
  scaleMax,
  label,
  tone = "primary",
}: {
  turns: Turn[];
  /** Shared across both charts so the comparison is honest. */
  scaleMax: number;
  label: string;
  tone?: "primary" | "success";
}) {
  const W = 520;
  const H = 170;
  const PAD = { top: 14, right: 8, bottom: 26, left: 46 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const max = Math.max(scaleMax, 1);
  const barW = Math.max(2, Math.min(34, plotW / Math.max(turns.length, 1) - 6));
  const step = turns.length ? plotW / turns.length : plotW;

  const schemaColour = tone === "primary" ? "#5eead4" : "#4ade80";
  const messageColour = "#a78bfa";

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <figure className="w-full">
      <figcaption className="mb-1 text-xs text-default-400">{label}</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={label}>
        {ticks.map((t) => {
          const y = PAD.top + plotH - (t / max) * plotH;
          return (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y}
                y2={y}
                stroke="rgba(232,236,244,0.09)"
                strokeWidth={1}
              />
              <text
                x={PAD.left - 6}
                y={y + 3}
                textAnchor="end"
                fontSize={9}
                fill="rgba(232,236,244,0.45)"
                fontFamily="ui-monospace, monospace"
              >
                {t >= 1000 ? `${Math.round(t / 1000)}k` : t}
              </text>
            </g>
          );
        })}

        {turns.map((turn, i) => {
          const x = PAD.left + i * step + (step - barW) / 2;
          const schemaH = (turn.schema.tokens / max) * plotH;
          const msgH = (turn.messages.tokens / max) * plotH;
          const schemaY = PAD.top + plotH - schemaH;
          const msgY = schemaY - msgH;

          return (
            <g key={turn.index}>
              <title>
                {`turn ${turn.index} — ${turn.action}\nschemas ${fmtNumber(turn.schema.tokens)} tok (${turn.toolsListed} tools)\nconversation ${fmtNumber(turn.messages.tokens)} tok`}
              </title>
              <rect x={x} y={msgY} width={barW} height={Math.max(msgH, 0)} fill={messageColour} opacity={0.55} rx={1} />
              <rect x={x} y={schemaY} width={barW} height={Math.max(schemaH, 1)} fill={schemaColour} rx={1} />
            </g>
          );
        })}

        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={PAD.top + plotH}
          y2={PAD.top + plotH}
          stroke="rgba(232,236,244,0.25)"
        />
        <text
          x={PAD.left}
          y={H - 8}
          fontSize={9}
          fill="rgba(232,236,244,0.45)"
          fontFamily="ui-monospace, monospace"
        >
          turn 1 → {turns.length}
        </text>
      </svg>

      <div className="mt-1 flex gap-4 text-[10px] text-default-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: schemaColour }} />
          tool schemas, re-sent every turn
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block h-2 w-2 rounded-sm opacity-60"
            style={{ background: messageColour }}
          />
          conversation
        </span>
      </div>
    </figure>
  );
}
